<template>
  <div class="payment-invoices-wrap">
    <!-- left size -->
    <div class="payment-invoices-wrap--left">
      <overlay-scrollbars :options="{ scrollbars: { autoHide: 'leave' }, overflowBehavior: { y: 'scroll', x: 'scroll' } }">
        <div style="display: flex; flex-wrap: wrap; gap: 1rem; margin-bottom: 1rem;">
          <!-- Invoice details -->
          <div style="flex: 1;">
            <div>
              <h4 class="text-nowrap">{{ $gettext('Invoice details') }}</h4>
            </div>
            <div class="d-flex" style="gap: 2rem;">
              <div>
                <div class="gk-modal-label">{{ $gettext('Sent date') }}</div>
                <div class="text-nowrap">
                  {{ invoice.sent_date | formatDate('DD-MM-YYYY') }}
                </div>
              </div>
              <div>
                <div class="gk-modal-label">{{ $gettext('Recipient') }}</div>
                {{ invoice.recipient_customer.name }}
              </div>
            </div>
          </div>

          <!-- Totals -->
          <div style="flex: 1;">
            <div class="d-flex align-items-center" style="gap: 1rem;">
              <h4 class="text-nowrap">{{ $gettext('Totals') }}<span v-if="invoice.currency !== 'EUR'"> (in &euro;)</span></h4>
              <GekkoButton
                v-if="invoice.currency !== 'EUR'"
                @click="setInvoiceTotalAmount(0)"
                :icon="'fa-regular fa-pen-to-square'"
                :text="$gettext('Update EUR amount')"
                size="sm"
              />
            </div>
            <div class="d-flex" style="gap: 2rem;">
              <div>
                <div class="gk-modal-label text-nowrap">{{ $gettext('Invoice total') }}</div>
                {{ invoice.total_amount | formatNumber }}
              </div>
              <div>
                <div class="gk-modal-label text-nowrap">{{ $gettext('Total paid') }}</div>
                {{ totalPaid | formatNumber }}
              </div>
              <div>
                <div class="gk-modal-label text-nowrap">{{ $gettext('Unpaid') }}</div>
                {{ invoice.total_amount - totalPaid | formatNumber }}
              </div>
            </div>
          </div>

          <div v-if="invoice.currency !== 'EUR'" style="flex: 1;">
            <div class="d-flex align-items-center" style="gap: 1rem;">
              <h4 class="text-nowrap">{{ $gettext('Totals') }}<span> (in {{ invoice.currency }})</span></h4>
            </div>
            <div class="d-flex" style="gap: 2rem;">
              <div>
                <div class="gk-modal-label text-nowrap">{{ $gettext('Invoice total') }}</div>
                {{ invoice.currency_total_amount | formatNumber }}
              </div>
            </div>
          </div>

        </div>

        <!-- Payments table -->
        <div style="padding-bottom: 1rem;">
          <div>
            <h4>{{ $gettext('Payments') }}<span v-if="invoice.currency !== 'EUR'"> (in &euro;)</span></h4>
          </div>
          <div>
            <b-table 
              :key="tableKey"
              :items="payments"
              :fields="paymentFields"
              class="invoice-payments-table"
              :class="['gk-table gk-table--responsive gk-table--headed gk-table--striped']"
              show-empty
            >
              <template #empty="">
                {{ $gettext('No payments for this invoice')}}
              </template>
              <template #cell(payment_date)="row">
                <span class="text-nowrap">
                  {{ row.item.payment_date | formatDate('DD-MM-YYYY') }}
                </span>
              </template>
              <template #cell(amount)="row">
                <span class="text-nowrap">
                  {{ row.item.amount | formatNumber }}
                </span>
              </template>
              <template #cell(payment_type)="row">
                <div v-if="row.item.payment_type === 'bank'">
                  <div v-if="row.item.transaction" class="d-inline-block">
                    <font-awesome-icon icon="fa-solid fa-university" :id="'tt-connected-transaction' + row.item.id" />
                    <b-tooltip :custom-class="'tooltip-padded'" :target="'tt-connected-transaction' + row.item.id" boundary="window" placement="top" triggers="hover">
                      <div class="tooltip-text">
                        {{ $gettext('Connected to a bank transaction') }}
                      </div>
                    </b-tooltip>
                  </div>
                  <div v-else class="d-inline-block">
                    <font-awesome-icon icon="fa-solid fa-building-columns" style="width: 30px;" />
                  </div>
                  {{ row.item.get_payment_type_display }}
                  <span v-if="row.item.sub_status === 'mollie'">{{ $gettext('via Mollie') }}</span>
                  <span v-if="row.item.sub_status === 'woocommerce'">{{ $gettext('via Woocommerce') }}</span>
                </div>

                <div v-if="row.item.payment_type === 'cash'">
                  <font-awesome-icon icon="fa-solid fa-sack-dollar" style="width: 30px;" />
                  {{ row.item.get_payment_type_display }}
                </div>
              </template>
              <template #cell(description)="row">
                {{ row.item.description }}
              </template>
              <template #cell(actions)="row">
                <GekkoButton 
                  icon="fa-solid fa-trash-can"
                  class="text-danger p-0"
                  variant="link"
                  v-gk-confirm="{
                    title: $gettext('Delete payment'),
                    message: $gettext('Are you sure you want to delete this payment?'),
                    options: {
                      variant: 'danger',
                      confirmText: $gettext('Delete')
                    },
                    callback: deletePayment,
                    callbackParams: row.item.id,
                  }"
                />
              </template>
            </b-table>
          </div>
        </div>
      </overlay-scrollbars>
    </div>
    <!-- right side -->
    <div v-if="this.isPaid" class="payment-invoices-wrap--right">
      <div style="padding: 1rem;">
        {{ $gettext('Invoice fully paid.') }}
      </div>
    </div>
    <div v-else class="payment-invoices-wrap--right gk-input-light">
      <overlay-scrollbars :options="{ scrollbars: { autoHide: 'leave' }, overflowBehavior: { y: 'scroll', x: 'scroll' } }">
        <!-- invoice convert currency total amount form -->
        <div v-if="invoice.currency !== 'EUR' && invoice.total_amount == 0.0" class="d-flex flex-column mb-3">
          <div>
            <h4>{{ $gettext('Convert to euro') }}</h4>
            <p style="font-size: 1rem;">{{ $gettext('This invoice is in a foreign currency, in order for it to be added to your gekko bookkeeping it will need to be converted to euro\'s. You can calculate the total through a set exchange rate or provide an amount manually.') }}</p>
          </div>
          <div>
            <b-form-group id="input-group-exchange" class="vat-input-size" label-class="gk-label-margin gk-modal-label" label-for="input-exchange">
              <template slot="label">
                {{ $gettext('Exchange rate') + ' (' + 'EUR' + ' / ' + invoice.currency + ')'}}
              </template>
              <b-input-group>
                <template #append>
                  <b-input-group-text class="btn" @click="exchangeRateTotal()">
                    <font-awesome-icon icon="fa-regular fa-calculator" class="append-icon" />
                  </b-input-group-text>
                </template>
                <b-form-input id="input-exchange" type="number" v-model="exchangeRate" placeholder="0.0" step="0.01" />
              </b-input-group>
            </b-form-group>
            <b-form-group id="input-group-invoice-amount" label-class="gk-label-margin gk-modal-label" :label="$gettext('Invoice total')" label-for="input-invoice-amount">
              <b-form-input id="input-invoice-amount" v-model="invoiceAmount" />
            </b-form-group>
          </div>
          <GekkoButton
            @click="updateEuroTotal"
            :text="$gettext('Update EUR total')"
            class="ml-auto"
          />
        </div>
        <!-- Payment add form -->
        <div class="d-flex flex-column mb-3" style="padding-bottom: 1rem;">
          <div>
            <h4>{{ $gettext('Add Payment') }}</h4>
          </div>
          <b-form-group id="input-group-amount" label-class="gk-label-margin gk-modal-label" :label="$gettext('Payment total')" label-for="input-amount">
            <b-form-input id="input-amount" v-model="newPayment.amount" />
            <div class="gk-input-error">
              {{ newPayment.errors.amount }}
            </div>
          </b-form-group>
          <b-form-group id="input-group-type" label-class="gk-label-margin gk-modal-label" :label="$gettext('Payment type')" label-for="input-type">
            <b-form-select v-model="newPayment.payment_type" :options="paymentTypeOptions" />
            <div class="gk-input-error">
              {{ newPayment.errors.payment_type }}
            </div>
            <div v-if="newPayment.payment_type === 'bank'" class="text-right">
              <GekkoButton
                :to="{ name: 'Bank' }"
                :text="$gettext('Link to existing transaction?')"
                icon="fa-solid fa-arrow-up-right-from-square"
                icon-right
                variant="link"
                size="sm"
              />
            </div>
          </b-form-group>
          <b-form-group id="input-group-date" label-class="gk-label-margin gk-modal-label" :label="$gettext('Payment date')" label-for="input-date">
              <b-form-datepicker id="input-date" class="inline-form-datepicker" v-model="newPayment.payment_date" no-flip initial-date="2021-01-01"
                                  :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }" />
          </b-form-group>
          <b-form-group id="input-group-description" label-class="gk-label-margin gk-modal-label" :label="$gettext('Description')" label-for="input-description">
            <b-form-input data-analytics-click id="input-description" v-model="newPayment.description" />
          </b-form-group>
          <GekkoButton
            @click="addPayment"
            :text="$gettext('Register payment')"
            class="ml-auto"
          />
        </div>
      </overlay-scrollbars>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";

import InvoiceService from "@/store/services/invoice.service.js"

import PaymentTypeSelector from "@/components/costs/PaymentTypeSelector"
import GekkoButton from "@/components/GekkoButton"

export default {
  name: 'InvoicePayments',
  components: { 
    GekkoButton,
    PaymentTypeSelector
  },
  props: {

  },
  data () {
    return {
      defaultPayment: {},
      newPayment: {},
      paymentTypeOptions: [
        { value: '', text: this.$gettext('Select a payment type') },
        { value: 'cash', text: this.$gettext('Cash or private account') },
        { value: 'bank', text: this.$gettext('Bank transaction') }
      ],
      paymentFields: [
        { key: "payment_date", label: this.$gettext('Date'), class: '' },
        { key: "amount", label: this.$gettext('Amount'), class: 'text-right' },
        { key: "payment_type", label: this.$gettext('Paid with'), class: '' },
        { key: "description", label: this.$gettext('Description'), class: '' },
        { key: "actions", label: '', class: '' },
      ],
      tableKey: 0,
      invoiceAmount: 0,
      exchangeRate: 0
    }
  },
  computed: {
    ...mapGetters({ 
      invoice: "invoices/getInvoice",
      payments: "invoices/getInvoicePayments",
      exchangeRates: "invoices/getExchangeRates",
    }),
    totalPaid () {
      let total = 0
      for (let index = 0; index < this.payments.length; index++) {
        total += parseFloat(this.payments[index].amount)
      }
      return total
    },
    isPaid () {
      return this.invoice.total_amount > 0 && this.invoice.total_amount == this.totalPaid
    }
  },
  watch: {
    invoice () {
      this._update_total()
    },
    newPayment: {
      handler(){
        console.log('newPayment watcher')
        let totalToBePaid = parseFloat(this.newPayment.amount) + this.totalPaid
        totalToBePaid = parseFloat(totalToBePaid.toFixed(2))
        const invoiceAmount = parseFloat(this.invoice.total_amount)
        if (this.invoice.total_amount > 0 &&  totalToBePaid > invoiceAmount) {
          this.$set(this.newPayment.errors, 'amount', this.$gettext('Total payments would exceed the invoice amount.'))
        } else if(this.invoice.total_amount < 0 && totalToBePaid < invoiceAmount) {
          this.$set(this.newPayment.errors, 'amount', this.$gettext('Total payments would exceed the invoice amount.'))
        } else {
          delete this.newPayment.errors.amount
        }
        if (this.newPayment.payment_type && this.newPayment.errors) {
          delete this.newPayment.errors.payment_type
        }
      },
      deep: true
    }

  },
  created () {
    this.setupData()
    this.$emit('display-buttons', '')
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  methods: {
    ...mapMutations({ 
      setInvoiceStatus: 'invoices/SET_INVOICE_STATUS',
      setInvoiceTotalAmount: 'invoices/SET_INVOICE_TOTAL_AMOUNT',
    }),
    ...mapActions({ 
      deleteStorePayment: 'invoices/deletePayment',
      addStorePayment: 'invoices/addPayment',
      loadExchangeRates: 'invoices/loadExchangeRates',
    }),
    close () {
      this.$emit('close')
      document.body.classList.remove('modal-on')
    },
    setupData () {
      this.invoiceAmount = this.invoice.total_amount

      this.defaultPayment = {
        invoice: this.invoice.id,
        amount: 0,
        payment_type: '',
        description: '',
        payment_date: new Date().toString("yyyy-MM-dd"),
        errors: {}
      }

      this.newPayment = Object.assign({}, this.defaultPayment)
      this._update_total()
      if (this.invoice.currency !== 'EUR') {
        if (!this.exchangeRates) {
          this.loadExchangeRates().then(() => {
            const this_rate = this.exchangeRates.find(item => item.to_currency === this.invoice.currency)
            if (this_rate) {
              this.exchangeRate = this_rate.exchange_rate
            }
          })
        } else {
          const this_rate = this.exchangeRates.find(item => item.to_currency === this.invoice.currency)
          if (this_rate) {
            this.exchangeRate = this_rate.exchange_rate
          }
        }
      }
    },
    deletePayment (payment_id) {
      this.deleteStorePayment(payment_id).then(()=> {
        this.tableKey++
        this._update_total()
        this._invoiceAmountIsValid()
        this.updateInvoiceStatus()

        this.$emit('invoice-updated')
      })
    },
    addPayment() {
      console.log('addPayment')
      if (!this.newPayment.amount) {
        this.newPayment.amount = 0
      }
      if ((typeof this.newPayment.amount) == 'string') {
        this.newPayment.amount = parseFloat(this.newPayment.amount.replace(',', '.'))
      }
      if (!this.newPayment.payment_type) {
        this.$set(this.newPayment.errors, 'payment_type', this.$gettext('Please select a payment type.'))
      } else {
        this.addStorePayment(this.newPayment).then(()=> {
          this._update_total()
          this.updateInvoiceStatus()
          this.newPayment = Object.assign({}, this.defaultPayment)

          this.$emit('invoice-updated')
        }).catch((response) => {
          this.$set(this.newPayment, 'errors', response.data)
        })
      }
    },
    updateInvoiceStatus () {
      if (this.isPaid) {
        this.setInvoiceStatus('p')
      } else {
        this.setInvoiceStatus('s')
      }
    },
    updateEuroTotal () {
      console.log('updateEuroTotal')
      if (this._invoiceAmountIsValid()) {
        InvoiceService.convertTotal(this.invoice.id, this.invoiceAmount).then(() => {
          this.$emit('invoice-updated')
          this.setInvoiceTotalAmount(this.invoiceAmount)
          this._update_total()
        })
      }
    },
    exchangeRateTotal () {
      if (this.exchangeRate && this.exchangeRate > 0) {
        this.invoiceAmount = parseFloat(this.invoice.currency_total_amount / this.exchangeRate).toFixed(2)
      } else {
        this.invoiceAmount = 0
      }
    },
    _update_total () {
      console.log('_update_total')
      this.newPayment.amount = Math.round((this.invoice.total_amount - this.totalPaid) * 100) / 100
      this.defaultPayment.amount = parseFloat(this.newPayment.amount).toFixed(2)
    },
    _invoiceAmountIsValid () {
      if ((this.invoiceAmount > 0 && this.invoiceAmount < this.totalPaid) || (this.invoiceAmount < 0 && this.invoiceAmount > this.totalPaid)) {
        this.newPayment.errors.invoiceAmount = this.$gettext("Payments for this invoice exceed the new total. Please delete one or more payments first.");
        return false;
      } else if (!this.invoiceAmount) {
        this.newPayment.errors.invoiceAmount = this.$gettext("This field is required.");
        return false;
      } else {
        delete this.newPayment.errors.invoiceAmount;
      }
      return true;
    }
  }
}
</script>

<style lang="scss">
  .gk-modal-payment-invoice {
    .gk-modal {
      max-width: 1200px;
      .action-buttons-wrapper {
        display: flex;
        gap: 0.75rem;
        justify-content: flex-end;
      }
    }
  }
</style>
<style lang="scss" scoped>
  .payment-invoices-wrap {
    display: flex;
    align-items: flex-start;
    flex-direction: column-reverse;
    &--left {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 1rem;
    }
    &--right {
      flex-direction: column;
      gap: 1rem;
      flex: 1;
      background: aliceblue;
    }
    &--left, &--right {
      width: 100%;
      max-width: 100%;
      .os-host { padding: 0.5rem; height: auto !important; }
    }
    @media (min-width: '768px') {
      overflow: hidden;
      flex-direction: row;
      height: 100%;
      &--left {
        height: 100%;
        width: 60%;
      }
      &--right {
        width: 40%;
        height: 100%;
        max-width: 450px;
      }
      &--left, &--right {
        .os-host { padding: 1rem; height: 100% !important; }
      }
    }
  }
</style>
